import './AudiencePenetration.scss';
import { useEffect, useMemo, useState } from 'react';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { useSetError } from '@src/redux/modals';
import { AudiencePenetrationCharts } from './components/AudiencePenetrationCharts';
import { AudiencePenetrationFilters } from './components/AudiencePenetrationFilters';
import { AudiencePenetrationTable } from './components/AudiencePenetrationTable';
import { DEFAULT_TOP_N } from './audience-penetration-constants';
import { AudiencePenetrationData, SegmentTypeType } from './audience-penetration-types';
import {
  AudiencePenetrationRowWithMetrics,
  filterRows,
  getSegmentTypeOptions,
  withMetrics
} from './utils/filter';
import { useAudiencePenetrationData } from './utils/useAudiencePenetrationData';

interface AudiencePenetrationContentProps {
  data: AudiencePenetrationData;
  selectedReportEndDate?: string;
  onPeriodChange: (reportEndDate: string) => void;
  isBusy: boolean;
}

const AudiencePenetrationContent = ({
  data,
  selectedReportEndDate,
  onPeriodChange,
  isBusy
}: AudiencePenetrationContentProps) => {
  // `segmentType` values come straight from the data (see getSegmentTypeOptions) rather than a
  // fixed enum. `null` means "no explicit choice made yet" — resolved to every currently-available
  // type below, so the default stays "all of them" even if a later period introduces a type that
  // didn't appear in whichever period was loaded when this state was first initialized. Once the
  // user makes an explicit selection it's pinned, same as every other filter on this page.
  const [segmentTypes, setSegmentTypes] = useState<SegmentTypeType[] | null>(null);
  // FilterBar hands back its own computed predicate (see AudiencePenetrationFilters.tsx) rather
  // than a raw search string; wrapped in the setter's updater form so React stores the function
  // itself as state instead of invoking it.
  const [nameFilter, setNameFilter] = useState<(row: AudiencePenetrationRowWithMetrics) => boolean>(
    () => () => true
  );
  const [topN, setTopN] = useState<number | null>(DEFAULT_TOP_N);
  const [hideOverfullReach, setHideOverfullReach] = useState(true);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | undefined>(undefined);

  const rows = useMemo(() => withMetrics(data.rows), [data.rows]);
  const segmentTypeOptions = useMemo(() => getSegmentTypeOptions(data.rows), [data.rows]);
  const effectiveSegmentTypes = segmentTypes ?? segmentTypeOptions.map(option => option.value);

  const filteredRows = useMemo(
    () => filterRows(rows, effectiveSegmentTypes, nameFilter, hideOverfullReach),
    [rows, effectiveSegmentTypes, nameFilter, hideOverfullReach]
  );

  const tableRows = selectedSegmentId
    ? filteredRows.filter(r => r.behaviorSegmentId === selectedSegmentId)
    : filteredRows;

  const onSelectSegment = (segmentId: string) => {
    setSelectedSegmentId(prev => (prev === segmentId ? undefined : segmentId));
  };

  return (
    <div className='audience-penetration'>
      <AudiencePenetrationFilters
        availablePeriods={data.availablePeriods}
        selectedReportEndDate={selectedReportEndDate}
        onPeriodChange={onPeriodChange}
        rows={rows}
        segmentTypeOptions={segmentTypeOptions}
        segmentTypes={effectiveSegmentTypes}
        onSegmentTypesChange={setSegmentTypes}
        onNameFilterChange={filter => setNameFilter(() => filter)}
        topN={topN}
        onTopNChange={setTopN}
        hideOverfullReach={hideOverfullReach}
        onHideOverfullReachChange={setHideOverfullReach}
      />
      <AudiencePenetrationCharts
        rows={filteredRows}
        topN={topN}
        selectedSegmentId={selectedSegmentId}
        onSelectSegment={onSelectSegment}
        isBusy={isBusy}
      />
      <AudiencePenetrationTable rows={tableRows} isBusy={isBusy} />
      {/* A real trailing element, not padding/margin on a flex ancestor — Chrome drops the
          trailing margin/padding of the last child from a flex-column `overflow: auto`
          container's scrollable-area calculation, so the space never becomes reachable by
          scrolling. An actual DOM node is unaffected by that and reliably reserves the room. */}
      <div className='audience-penetration-bottom-spacer' />
    </div>
  );
};

interface AudiencePenetrationProps {
  amcInstanceId?: string;
}

export const AudiencePenetration = ({ amcInstanceId }: AudiencePenetrationProps) => {
  const [reportEndDate, setReportEndDate] = useState<string | undefined>(undefined);
  const { data, isLoading, isFetching, error } = useAudiencePenetrationData(
    amcInstanceId,
    reportEndDate
  );
  const setError = useSetError();

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load AMC data",
        message: `Failed to load audience penetration data: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  // The very first fetch has no reportEndDate to send (state starts undefined), so the backend
  // resolves its own default period (`availablePeriods[0]`) rather than an explicit date match —
  // a different code path with no guarantee it returns the same rows an explicit request for that
  // same period would. Sync the state to the server's own choice as soon as it's known, so every
  // fetch from here on — including any later refetch once the cache entry expires — always sends
  // an explicit reportEndDate instead of occasionally re-hitting the ambiguous default branch.
  useEffect(() => {
    if (!reportEndDate && data?.selectedPeriod) {
      setReportEndDate(data.selectedPeriod.reportEndDate);
    }
  }, [reportEndDate, data]);

  if (isLoading) return <FullPageSpinner />;

  if (!data || data.availablePeriods.length === 0) {
    return (
      <div className='audience-penetration-empty'>
        Audience Penetration isn’t available for this client.
      </div>
    );
  }

  // Reflect the user's own selection immediately rather than waiting on the refetch to resolve
  // (keepPreviousData means `data` briefly still holds the prior period's response); fall back to
  // the server's default only for the brief window before the sync effect above catches up.
  const selectedReportEndDate = reportEndDate ?? data.selectedPeriod?.reportEndDate;

  return (
    <AudiencePenetrationContent
      data={data}
      selectedReportEndDate={selectedReportEndDate}
      onPeriodChange={setReportEndDate}
      isBusy={isFetching}
    />
  );
};
